// Settings forms generated from the contracts settings registry (plan §4.7, kind A pages). The control for a key
// comes from its registry schema (boolean, enum, Rate, Money, integer, quarter-hour time, the tip-preset tuple); a
// layout override only pins legacy's page, section order, headings, copy, the control flavour (radio vs checkbox)
// and conditional visibility. A key whose schema has no control fails the layout test instead of rendering wrong.
import { z } from 'zod';
import { Money, QuarterHourTime, Rate, settings, type SettingKey } from '@radish/contracts';
import type { AppPath } from '../../router-paths.ts';

type Control =
  | { readonly kind: 'checkbox' }
  | { readonly kind: 'choice'; readonly options: readonly string[] }
  | { readonly kind: 'percent' }
  | { readonly kind: 'money' }
  | { readonly kind: 'integer'; readonly min: number; readonly max: number }
  | { readonly kind: 'quarter-hour' }
  | { readonly kind: 'percent-presets'; readonly count: number }
  /** A list of ids picked from the location's records (the layout's `choicesFrom` names which). */
  | { readonly kind: 'ids' }
  | { readonly kind: 'unsupported'; readonly reason: string };

interface SchemaDef {
  readonly type: string;
  readonly entries?: Readonly<Record<string, string>>;
  readonly items?: readonly z.ZodType[];
}

const defOf = (schema: z.ZodType): SchemaDef => (schema as unknown as { _zod: { def: SchemaDef } })._zod.def;

/** The bounds a Zod integer schema carries (`z.int().min(60).max(1440)`). */
function boundsOf(schema: z.ZodType): { readonly min: number; readonly max: number } {
  const n = schema as unknown as { minValue: number | null; maxValue: number | null };
  return { min: n.minValue ?? 0, max: n.maxValue ?? Number.MAX_SAFE_INTEGER };
}

/** The control a registry key's schema calls for. */
export function controlOf(key: SettingKey): Control {
  const schema: z.ZodType = settings[key].schema;
  if (schema === Rate) return { kind: 'percent' };
  if (schema === Money) return { kind: 'money' };
  if (schema === QuarterHourTime) return { kind: 'quarter-hour' };
  const def = defOf(schema);
  switch (def.type) {
    case 'boolean':
      return { kind: 'checkbox' };
    case 'enum':
      return { kind: 'choice', options: Object.values(def.entries ?? {}) };
    case 'number':
      return { kind: 'integer', ...boundsOf(schema) };
    case 'tuple': {
      const items = def.items ?? [];
      return items.every((i) => defOf(i).type === 'number') ? { kind: 'percent-presets', count: items.length } : { kind: 'unsupported', reason: 'tuple of non-integers' };
    }
    case 'array':
      return { kind: 'ids' };
    default:
      return { kind: 'unsupported', reason: `no control for a ${def.type} schema` };
  }
}

export type SettingValues = Readonly<Partial<Record<SettingKey, unknown>>>;

export interface LayoutField {
  readonly key: SettingKey;
  /** The legacy label; defaults to the registry label. */
  readonly label?: string;
  readonly description?: string;
  /** Legacy's info line under the description (`settings-hint`, with its info icon). */
  readonly hint?: string;
  /** Booleans as two radios (legacy "Tip Amounts"); enums default to radios. */
  readonly flavour?: 'radio' | 'select';
  /** Labels for enum values (or "true" / "false" for a boolean shown as radios). */
  readonly options?: Readonly<Record<string, string>>;
  /** Minutes keys typed as hours (legacy stored hours: 8, 40, 12). */
  readonly unit?: 'hours';
  readonly visibleWhen?: (values: SettingValues) => boolean;
  /** Where an id-list setting's choices come from (legacy q-select `multiple` with `use-chips` over those records). */
  readonly choicesFrom?: 'dining-options';
  /** The field's own hint under its box (legacy q-select `hint`), unlike `hint`'s info line under the description. */
  readonly fieldHint?: string;
  /**
   * The field's columns from 600 px in legacy's `q-pl-sm row q-col-gutter-md` (`col-xs-12 col-sm-N`, Auto Gratuity's
   * threshold, percentage and dining options, B1948): consecutive fields with `cols` share one row 8 px in, 16 px apart,
   * each full width under 600.
   */
  readonly cols?: 4 | 8;
  /**
   * A rate legacy types as a whole percent through MoneyInput `mask="##"` with `prefix=""` (Auto Gratuity's percentage,
   * B1949): two digits at most (0 to 99), no decimals, "00" at 0, and legacy's placeholder. The setting stays a Rate.
   */
  readonly wholePercent?: { readonly placeholder: string };
  /**
   * A boolean legacy draws as a UiCustomSetting with a DENSE BaseCheckbox (ui-web `CheckboxSetting`: the 18 px box at the
   * field column's edge, the bold title and description 12 px after it); `'title-gap'` when the title carries legacy's
   * `q-mb-sm`. Unset: `Checkbox settingDescription` (legacy's plain 40 px BaseCheckbox, the box 11 px in).
   * Not with `hint` (the dense branch draws no info line; settings-layouts.test.ts refuses the pair), and ignored by a
   * `stacked` layout, which draws its own checkbox.
   */
  readonly denseCheckbox?: true | 'title-gap';
  /**
   * A boolean legacy draws as a labelled BaseCheckbox (`class="text-weight-medium" :label=…`, Staff Tips' Payroll Tips and
   * Tip Tracking): the 20 px box in a 40 px cell, the label at medium weight in the secondary colour right after it,
   * centred in the 40 px row, and the description under the row indented 40 px (`q-ml-lg q-pl-md`). Not with `hint`.
   */
  readonly labelledCheckbox?: true;
  /**
   * A plain checkbox setting whose legacy title keeps its `q-mb-sm` with no description under it (Register Settings'
   * Re-Order included modifiers, B2069): legacy's description column is the 21 px title plus 8 px, so the row is 29 px
   * where the checkbox cell alone is 24.
   */
  readonly titleMargin?: true;
  /** Radios as a bare legacy q-option-group (the checked label at normal weight; ui-web RadioGroup `plainLabels`). */
  readonly plainLabels?: true;
  /**
   * An integer legacy draws as a UiCustomSetting of its own under a checkbox setting, its action an invisible twin of that
   * checkbox (Register Settings' Auto-print within, B1904): the bold `label` lined up under the checkbox's title, 8 px,
   * the `description`, 16 px, then a dense outlined q-input 160 px wide with its own label and suffix, and 20 px kept under
   * it for the rules' message. The rules are legacy's q-input `rules` over the typed text, first failure shown; while one
   * fails the page's Save saves nothing, as legacy's q-form `validate()` refuses.
   */
  readonly underCheckbox?: {
    readonly fieldLabel: string;
    readonly suffix?: string;
    readonly rules: readonly ((text: string) => true | string)[];
  };
}

/** The first failing rule's message, as Quasar shows it; undefined while every rule holds. */
export function ruleError(rules: readonly ((text: string) => true | string)[], text: string): string | undefined {
  for (const rule of rules) {
    const result = rule(text);
    if (result !== true) return result;
  }
  return undefined;
}

export interface LayoutSection {
  readonly heading: string;
  readonly description?: string;
  readonly fields: readonly LayoutField[];
  /** Shown only when this holds over the page's values, with `reads` (Print Options' coursing while courses are on). */
  readonly visibleWhen?: (values: SettingValues) => boolean;
  /**
   * Legacy UiCustomSetting with no action: the description and the fields in its 10 px description column (Pacing's
   * Course Prompt), instead of 24 px apart.
   */
  readonly tight?: true;
  /**
   * Legacy's q-separator between this section's settings on a page whose other sections stack theirs unruled
   * (RegisterSettings.vue's Tables, Tax Settings and Kitchen, B1499).
   */
  readonly fieldRules?: true;
}

export interface SettingsLayout {
  readonly id: string;
  readonly screenId: string;
  readonly tabs?: readonly { readonly label: string; readonly to: AppPath }[];
  /** The tab bar's accessible name ("Tips", "POS Functions"). */
  readonly tabsLabel?: string;
  readonly intro?: string;
  readonly sections: readonly LayoutSection[];
  /** False: a section's fields sit under no rule (legacy pages that stack their UiCustomSettings without q-separators). */
  readonly fieldRules?: boolean;
  /** Keys the page reads to decide what shows, never edits or saves. */
  readonly reads?: readonly SettingKey[];
  /**
   * Legacy's one-column settings stack (`ui-settings-stack`, e.g. Timecards › Report Settings): each section under a rule
   * with its 17.6 px PageHeader heading, the description, then the fields, and a rule over Save; no heading column.
   */
  readonly stacked?: boolean;
}

/** Every key a layout renders. */
export const keysOf = (layout: SettingsLayout): SettingKey[] => layout.sections.flatMap((s) => s.fields.map((f) => f.key));

/** The keys whose value differs from what was loaded (a save sends only these, plan §4.7). */
export function dirtyKeys(loaded: SettingValues, current: SettingValues, keys: readonly SettingKey[]): SettingKey[] {
  const wire = (key: SettingKey, value: unknown) => JSON.stringify(z.encode(settings[key].schema as z.ZodType, value as never));
  return keys.filter((k) => current[k] !== undefined && wire(k, loaded[k]) !== wire(k, current[k]));
}
